import { useState } from 'react';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { useElementOnScreen } from 'src/hooks/use-intersection-observer';

import { fCurrency } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { useGetReviews } from 'src/actions/reviews';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { ReviewDrawer } from 'src/components/reviews';
import { ColorPreview } from 'src/components/color-utils';
import { ShowMoreLessText } from 'src/components/text-utils';
import {
  ButtonBlur,
  ButtonShare,
  ButtonComments,
  ButtonAddToCart,
  ButtonAddFavorite,
} from 'src/components/custom-buttons';

import { GalleryCarousel } from './components/gallery-carousel';

// ----------------------------------------------------------------------

const collection = 'promotions';
const placeholderImages = [{ src: '/assets/core/placeholder-gallery.webp', type: 'image/webp' }];

export function ProductItem({ product, preview, searchParams }) {
  const { t } = useTranslate();

  const showReview = useBoolean();

  const [showMore, setShowMoreLess] = useState(false);
  const [openReviews, setOpenReviews] = useState(false);

  const { containerRef, isVisible } = useElementOnScreen({
    root: null,
    threshold: 1.0,
    rootMargin: '0px',
  });

  const {
    id,
    name,
    price,
    colors,
    images,
    country,
    shipping,
    newLabel,
    saleLabel,
    available,
    priceSale,
    totalLikes,
    totalSales,
    totalRating,
    totalShared,
    numberFormat,
    totalReviews,
    shortDescription,
  } = product;

  const reviewHook = useGetReviews(id, collection, openReviews);

  const gallery = preview && !images.length ? placeholderImages : images;

  const renderLabels = () => (
    <Box
      sx={{
        pt: 2,
        gap: 1,
        left: 0,
        px: 1.5,
        width: 1,
        zIndex: 99,
        display: 'flex',
        position: 'absolute',
      }}
    >
      {newLabel?.enabled && (
        <Label variant="filled" color="info">
          {t('NEW')}
        </Label>
      )}

      {shipping && (
        <Label variant="filled" color="success">
          <Iconify icon="carbon:delivery-parcel" sx={{ mr: 0.5 }} />
          {t('Free shipping')}
        </Label>
      )}

      {saleLabel?.enabled && (
        <Label variant="filled" color="error">
          {`-${saleLabel?.content ?? 0}%`}
        </Label>
      )}
    </Box>
  );

  const renderButtons = () => (
    <Box
      className="product__actions"
      sx={{
        zIndex: 999,
        py: { sm: 1 },
        display: 'flex',
        height: { sm: 1 },
        position: 'absolute',
        gap: { xs: 2, sm: 1 },
        flexDirection: 'column',
        right: { xs: 0, sm: 10 },
        width: { xs: 64, sm: 'fit-content' },
        justifyContent: { xs: 'end', sm: 'center' },
      }}
    >
      <ButtonAddFavorite
        id={id}
        preview={preview}
        totalLikes={totalLikes}
        isLiked={searchParams?.list === 'favorites'}
      />
      <ButtonComments
        totalComments={openReviews ? reviewHook.totalReviews : totalReviews}
        onClick={() => {
          if (!preview) {
            if (!openReviews) setOpenReviews(true);
            showReview.onTrue();
          }
        }}
      />
      <ButtonShare id={id} preview={preview} country={country} totalShared={totalShared} />
      <ButtonAddToCart preview={preview} product={product} totalSales={totalSales} />
    </Box>
  );

  const renderGallery = () => (
    <Box id={`gallery-slider-${id}`} sx={{ position: 'relative', px: { xs: 0, sm: 1 } }}>
      {renderLabels()}
      {renderButtons()}
      {preview && (
        <ButtonBlur
          text="Preview"
          sx={{
            zIndex: 999,
            right: '50%',
            position: 'absolute',
            top: { xs: 54, sm: 'auto' },
            transform: 'translateX(50%)',
            bottom: { xs: 'auto', sm: 16 },
          }}
        />
      )}

      <Tooltip title={!available && t('Out of stock')} placement="bottom-end">
        <GalleryCarousel
          id={id}
          alt={name}
          gallery={gallery}
          showMore={showMore}
          available={available}
          isVisible={isVisible}
        />
      </Tooltip>
    </Box>
  );

  const renderDescription = () => (
    <Stack spacing={1} className="product__description" sx={{ px: 2, py: 2.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <ColorPreview
          colors={colors}
          sx={[(theme) => ({ [theme.breakpoints.down('sm')]: { ml: 0.5 } })]}
        />

        <Stack
          direction="row"
          spacing={0.5}
          sx={{ typography: { xs: 'h5', sm: 'h6', lg: 'subtitle1' } }}
        >
          {saleLabel?.enabled && (
            <Box component="span" sx={{ opacity: 0.7, textDecoration: 'line-through' }}>
              {fCurrency(price, numberFormat)}
            </Box>
          )}

          <Box component="span">{fCurrency(priceSale, numberFormat)}</Box>
        </Stack>
      </Stack>

      <Tooltip title={name} placement="bottom-end">
        {preview ? (
          <Typography noWrap variant="subtitle2">
            {name}
          </Typography>
        ) : (
          <Link
            noWrap
            color="inherit"
            variant="subtitle2"
            component={RouterLink}
            href={`${paths.product.details(id)}${newSearchParams({ country })}`}
          >
            {name}
          </Link>
        )}
      </Tooltip>

      <ShowMoreLessText text={shortDescription} onClick={setShowMoreLess} sx={{ opacity: 0.7 }} />

      <Rating
        readOnly
        size="small"
        precision={0.1}
        value={openReviews ? reviewHook.totalRating : totalRating}
      />
    </Stack>
  );

  const renderReviewDrawer = () => (
    <ReviewDrawer id={id} collection={collection} reviewHook={reviewHook} showReview={showReview} />
  );

  return (
    <Card ref={containerRef} className="scroll__snap__child">
      {renderGallery()}
      {renderDescription()}
      {!preview && renderReviewDrawer()}
    </Card>
  );
}
